import Guide, { IGuide, IStep as $IStep } from "byte-guide";
import { useCallback, useEffect, useState } from "react";
import { defineMessages, useIntl } from "react-intl";

import { useUserSettings } from "../../../lib/hooks/basic/users";
import { VIDEO_TOUR_NAME } from "../VideoModal";

export interface IStep extends $IStep {
  fixedBody?: boolean;
}

const messages = defineMessages({
  step: {
    id: "onboarding.tour.step",
    defaultMessage: "Step {stepIndex} of {stepCount}",
  },
  ok: {
    id: "ok",
    defaultMessage: "OK",
  },
});

export default function ProductTour({
  beforeStepChange,
  afterStepChange,
  onClose,
  stepsWithFixedBody,
  ...props
}: Omit<IGuide, "steps"> & {
  steps: IStep[];
  stepsWithFixedBody?: string[];
}) {
  const intl = useIntl();
  const { tourShown, markProductTourStepCompleted } = useUserSettings();
  const [hidden, setHidden] = useState(true);

  useEffect(() => {
    setHidden(
      !props.localKey ||
        !!localStorage.getItem(VIDEO_TOUR_NAME) ||
        tourShown(props.localKey)
    );
  }, [props.localKey, tourShown]);

  // otherwise, the body will scroll when the guide between steps
  const setFixedBody = useCallback((step?: IStep) => {
    if (!step) {
      document.body.style.position = "";
      return;
    }

    const selector = step.selector;
    const anchor =
      typeof selector === "string"
        ? document.querySelector(selector)
        : typeof selector === "function"
          ? selector()
          : selector;

    document.body.style.position =
      step.fixedBody && (anchor || step.targetPos) ? "fixed" : "";
  }, []);

  useEffect(() => {
    if (hidden) setFixedBody();

    return () => setFixedBody();
  }, [hidden, setFixedBody]);

  if (hidden) return null;

  return (
    <Guide
      hotspot
      showPreviousBtn
      stepText={(stepIndex, stepCount) =>
        intl.formatMessage(messages.step, { stepIndex, stepCount })
      }
      nextText={intl.formatMessage({ id: "next" })}
      prevText={intl.formatMessage({ id: "back" })}
      okText={intl.formatMessage(messages.ok)}
      maskClassName="product-tour-mask"
      beforeStepChange={(idx: number, step: IStep) => {
        setFixedBody(props.steps[idx + 1]);
        if (beforeStepChange) beforeStepChange(idx, step);
      }}
      afterStepChange={(idx: number, step: IStep) => {
        setFixedBody(step);
        if (afterStepChange) afterStepChange(idx, step);
      }}
      onClose={() => {
        setFixedBody();
        markProductTourStepCompleted(props.localKey);
        if (onClose) onClose();
      }}
      {...props}
    />
  );
}
